import { formPdf, finishPdf } from './formPdf';

const usDate = (v) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || '')); return m ? `${m[2]}/${m[3]}/${m[1]}` : String(v || ''); };
const NA = /^\s*not (available|provided|specified|documented)\b/i;
const str = (v) => {
  if (v == null || NA.test(String(v))) return '';
  if (Array.isArray(v)) return v.map(str).filter(Boolean).join('\n');
  if (typeof v === 'object') return Object.entries(v).map(([k, x]) => `${k}: ${str(x)}`).join(', ');
  return String(v);
};
const pick = (o, ...k) => str(k.map(x => o[x]).find(v => v != null && v !== ''));
// one row per medication: name | dose | route | frequency | instructions
export const medRows = (d) => {
  const m = d.medications;
  if (!m || (typeof m === 'string' && !m.trim())) return [];
  const list = Array.isArray(m) ? m : (typeof m === 'object' ? [m] : String(m).split(/\n|;|(?:^|\s)\d+[.)]\s/).map(x => x.trim()).filter(Boolean));
  const rows = list.map(x => (x && typeof x === 'object')
    ? [pick(x, 'name', 'medication', 'drug'), pick(x, 'dose', 'dosage', 'strength'), pick(x, 'route'), pick(x, 'frequency'), pick(x, 'instructions', 'instruction', 'indication', 'notes', 'status')]
    : [str(x), '', '', '', '']).filter(r => r.some(Boolean));
  if (rows.length === 1) ['dosage', 'route', 'frequency'].forEach((k, i) => { if (!rows[0][i + 1] && d[k]) rows[0][i + 1] = str(d[k]); });
  return rows;
};
let ruler = null;
// real wrapped line count at the fill font size; `frac` = share of the 188mm row width
const est = (t, frac) => {
  if (!ruler) return String(t || '').split('\n').reduce((s, ln) => s + Math.max(1, Math.ceil(ln.length / (frac * 110))), 0);
  ruler.setFontSize(9);
  return ruler.splitTextToSize(String(t || ''), 188 * frac - 3).length;
};
const CHUNK = 30;
// split long text into page-sized pieces (by line, then by word) so nothing is ever cut off
const chunks = (text, chars = 1, max = CHUNK) => {
  const out = []; let cur = '';
  const push = (piece) => {
    const next = cur ? `${cur}\n${piece}` : piece;
    if (est(next, chars) <= max) { cur = next; return; }
    if (cur) out.push(cur);
    cur = piece;
    while (est(cur, chars) > max) {
      const words = cur.split(' '); let head = '';
      while (words.length && est(head ? `${head} ${words[0]}` : words[0], chars) <= max) head = head ? `${head} ${words.shift()}` : words.shift();
      if (!head) head = words.shift();
      out.push(head); cur = words.join(' ');
    }
  };
  String(text).split('\n').forEach(push);
  out.push(cur);
  return out;
};
const TYPES = ['New order', 'Change in order', 'Discontinue', 'Verbal order', 'Written order'];

// Physician / doctor's order laid out like a real order sheet: header, patient block, numbered order lines, read-back, signatures
const LINES = {
  default: (d) => [
    ['Treatments', d.treatments], ['Frequency / duration', [d.frequency, d.duration].filter(Boolean)], ['Labs', d.labOrders],
    ['Diet', d.dietOrders], ['Activity', d.activityRestrictions], ['Special instructions', [d.specialInstructions, d.followUpInstructions].filter(Boolean)],
  ],
  therapy: (d) => [
    ['Discipline(s)', d.disciplines], ['Evaluate and treat / reason', [d.evaluateAndTreat, d.reasonForTherapy].filter(Boolean)],
    ['Frequency / duration', [d.frequency, d.duration].filter(Boolean)], ['Interventions', d.interventions], ['Goals', d.goals],
    ['Precautions / weight bearing', [d.precautions, d.weightBearing].filter(Boolean)], ['Special instructions', d.specialInstructions],
  ],
  medication: (d) => [
    ['Allergies', d.allergies], ['Pharmacy', [d.pharmacy, d.pharmacyPhone].filter(Boolean)], ['Reason / indication', d.reason],
    ['Special instructions', [d.specialInstructions, d.monitoring].filter(Boolean)],
  ],
  wound: (d) => [
    ['Wound', [d.woundNumber && `Wound #${str(d.woundNumber)}`, d.woundLocation && `Location: ${str(d.woundLocation)}`, d.woundType && `Type: ${str(d.woundType)}`,
      d.pressureStage && `Stage: ${str(d.pressureStage)}`, [d.length, d.width, d.depth].some(x => str(x)) && `Size (L x W x D): ${[d.length, d.width, d.depth].map(x => str(x).replace(/\s*cm\s*$/i, '') || '-').join(' x ')} cm`].filter(Boolean)],
    ['Treatment', [d.cleanser && `Cleanse with: ${str(d.cleanser)}`, d.primaryDressing && `Primary dressing: ${str(d.primaryDressing)}`, d.secondaryDressing && `Secondary dressing / cover: ${str(d.secondaryDressing)}`,
      d.periwoundCare && `Periwound care: ${str(d.periwoundCare)}`, str(d.additionalTherapy) && `Additional: ${str(d.additionalTherapy).replace(/\n/g, ', ')}`].filter(x => x && !/:\s*$/.test(x))],
    ['Frequency / duration', [d.frequency && `Change: ${str(d.frequency)}`, d.duration && `Duration: ${str(d.duration)}`, d.startDate && `Start: ${usDate(d.startDate)}`].filter(Boolean)],
    ['Skilled nursing & teaching', [d.snFrequency && `SN visits: ${str(d.snFrequency)}`, d.caregiverTeaching && `Patient / caregiver taught wound care: ${str(d.caregiverTeaching)}`].filter(Boolean)],
    ['Notify physician if', d.notifyPhysicianIf], ['Special instructions', d.specialInstructions],
  ],
  dme: (d) => [
    ['Item(s) ordered', d.items], ['Quantity / size', d.quantity], ['HCPCS code(s)', d.hcpcsCodes], ['Medical necessity / diagnosis', [d.medicalNecessity, d.diagnosis].filter(Boolean)],
    ['Length of need', d.lengthOfNeed], ['Supplier / vendor', [d.supplier, d.supplierPhone].filter(Boolean)], ['Special instructions', d.specialInstructions],
  ],
};

export const buildOrderPdf = (title, d = {}, ctx = {}, kind = 'default') => {
  const f = formPdf(title.toUpperCase(), 'Home Health Physician Order');
  const doc = f.doc;
  ruler = doc;
  doc.patientInfo = { name: ctx.patientName || d.patientName, mrn: ctx.mrn, dob: ctx.dateOfBirth, dx: ctx.primaryDiagnosis, admission: ctx.startOfCareDate };
  f.row([['Patient Name', ctx.patientName || str(d.patientName), 0.4], ['DOB', usDate(ctx.dateOfBirth), 0.2], ['MR#', ctx.mrn || '', 0.2], ['SOC', usDate(ctx.startOfCareDate), 0.2]]);
  f.row([['Diagnosis / ICD-10', str(ctx.primaryDiagnosis || d.diagnosis), 0.6], ['Order date', usDate(d.orderDate) || new Date().toLocaleDateString('en-US'), 0.2], ['Time', str(d.orderTime), 0.2]]);
  const type = String(d.orderType || (d.verbalOrder ? 'Verbal order' : '')).toLowerCase();
  f.section('Order type');
  f.checks(TYPES.map(t => [t, Boolean(type) && type.includes(t.split(' ')[0].toLowerCase())]));
  f.section('Orders');
  // main order items (Treatments, Labs, Special instructions...) in bold; medications always come last
  let n = 0;
  (LINES[kind] || LINES.default)(d).forEach(([label, v]) => {
    n += 1;
    chunks(str(v)).forEach((text, i) => {
      f.row([[`${n}. ${label.toUpperCase()}${i ? ' (cont.)' : ''}`, text, 1, { lines: Math.max(Math.ceil(est(text, 1) * 0.88), 1), bold: true }]]);
    });
  });
  if (kind === 'default' || kind === 'medication') {
    n += 1;
    const meds = medRows(d);
    const W = [0.28, 0.12, 0.1, 0.16, 0.34];
    const body = meds.length ? meds : [['', '', '', '', '']];
    f.space(14);
    f.para(`${n}. MEDICATIONS${meds.length ? ` (${meds.length})` : ''}`, { bold: true, size: 9, color: [30, 60, 120] });
    body.forEach((r, i) => {
      const first = meds.length > 1 ? `Medication ${i + 1} of ${meds.length}` : 'Medication';
      const labels = [first, 'Dose', 'Route', 'Frequency', 'Instructions'];
      const parts = r.map((v, j) => chunks(v, W[j]));
      const count = Math.max(...parts.map(p => p.length));
      for (let k = 0; k < count; k += 1) {
        const cells = parts.map(p => p[k] || '');
        const lines = Math.max(1, Math.ceil(Math.max(...cells.map((v, j) => est(v, W[j]))) * 0.88));
        f.row(cells.map((v, j) => [k ? (j === 0 ? `${first} (cont.)` : '') : labels[j], v, W[j], { lines }]));
      }
    });
  }
  f.row([['Urgency', str(d.urgency), 0.3], ['Received by (nurse / credentials)', str(d.receivedBy), 0.7]]);
  f.checks([['Order read back and verified with physician', (str(d.readBack) ? /^yes/i.test(str(d.readBack)) : Boolean(/^(yes|true)$/i.test(str(d.verbalOrder)) || type.includes('verbal')))], ['Patient / caregiver notified', false]]);
  f.section('Physician');
  f.row([['Physician name', str(d.orderingPhysician || d.physicianName || ctx.primaryPhysician), 0.4], ['NPI', str(d.physicianNPI), 0.2], ['Phone', str(d.physicianPhone || ctx.primaryPhysicianPhone), 0.2], ['Fax', str(d.physicianFax || ctx.physicianFax), 0.2]]);
  // no physician signature line: the physician signs electronically
  f.space(10);
  f.para('Electronically signed by the physician', { bold: true, size: 10, color: [30, 60, 120] });
  f.signatures(['Date signed', 'Nurse signature / date'], [{ text: usDate(d.physicianSignatureDate) }, { text: [str(d.nurseSignature), usDate(d.nurseSignatureDate)].filter(Boolean).join('  ') }]);
  f.para('Physician: please sign, date and return to IVORY Home Health Care. Fax (562) 285-7216.', { size: 7.5 });
  return finishPdf(doc, title);
};
